import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Container,
  Avatar,
  Grid,
  LinearProgress,
  alpha,
  Card,
  CardContent,
  TextField,
} from '@mui/material';
import {
  Science as ScienceIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { axiosService } from 'shared/helpers/axios';
import BackButton from '../../common/buttons/BackButton';

const ExpertCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  height: '100%',
  minHeight: '120px',
  '&:hover': {
    transform: 'translateX(4px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.12)}`,
    borderColor: alpha(theme.palette.primary.main, 0.4),
  },
}));

const AllSuggestedExpertsPage = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [suggestedExperts, setSuggestedExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [call, setCall] = useState(null);
  
  // Filter state
  const [inputValues, setInputValues] = useState({
    affiliation: '',
    keywords: '',
    country: ''
  });
  const [filters, setFilters] = useState({
    affiliation: '',
    keywords: '',
    country: ''
  });

  // Debounce effect - 400ms delay before applying filters
  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(inputValues);
    }, 400);
    return () => clearTimeout(timeout);
  }, [inputValues]);

  // Reset loading when filters change (except initial load)
  useEffect(() => {
    if (filters.affiliation || filters.keywords || filters.country) {
      setLoading(true);
    }
  }, [filters]);

  useEffect(() => {
    const fetchCallDetails = async () => {
      try {
        const response = await axiosService.get(`/funding/calls/${callId}/`);
        setCall(response.data);
      } catch (error) {
        console.error('Error fetching call details:', error);
      }
    };

    const fetchSuggestedExperts = async () => {
      try {
        const params = new URLSearchParams();
        
        // Combine search and keywords into the search parameter
        const searchTerms = [filters.keywords].filter(Boolean).join(' ');
        if (searchTerms) params.append('search', searchTerms);
        
        // Add affiliation and country filters
        if (filters.affiliation) params.append('affiliation', filters.affiliation);
        if (filters.country) params.append('country', filters.country);
        
        const queryString = params.toString();
        const url = queryString 
          ? `/funding/calls/${callId}/suggested_experts/?${queryString}`
          : `/funding/calls/${callId}/suggested_experts/`;
        
        const response = await axiosService.get(url);
        setSuggestedExperts(response.data || []);
      } catch (error) {
        console.error('Error fetching suggested experts:', error);
        setSuggestedExperts([]);
      } finally {
        setLoading(false);
      }
    };

    if (callId) {
      fetchCallDetails();
      fetchSuggestedExperts();
    }
  }, [callId, filters]);

  const handleExpertClick = (expertId) => {
    navigate(`/profile/${expertId}`);
  };

  const handleBackClick = () => {
    navigate(`/funding/call/${callId}`);
  };

  const handleInputChange = (field, value) => {
    setInputValues(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ flex: 1, letterSpacing: 0.2 }} gutterBottom>
            <BackButton position="inline" /> Suggested Experts
          </Typography>
          <Typography variant="body1" color="text.secondary" ml={4}>
            {call ? `For: ${call.title}` : 'Researchers matching call skills and research interests'}
          </Typography>
        </Box>
      </Box>

      {/* Filter Panel */}
      <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              minWidth: 'fit-content',
              fontWeight: 500,
              color: 'text.secondary'
            }}
          >
            Filter By
          </Typography>
          <Box display="flex" gap={2} flex={1}>
            <TextField
              label="Affiliation"
              value={inputValues.affiliation || ''}
              onChange={(e) => handleInputChange('affiliation', e.target.value)}
              placeholder="Filter by affiliation..."
              size="small"
              fullWidth
            />
            <TextField
              label="Skills & Expertise"
              value={inputValues.keywords || ''}
              onChange={(e) => handleInputChange('keywords', e.target.value)}
              placeholder="Filter by skills, expertise, or research interests..."
              size="small"
              fullWidth
            />
            <TextField
              label="Country"
              value={inputValues.country || ''}
              onChange={(e) => handleInputChange('country', e.target.value)}
              placeholder="Filter by country..."
              size="small"
              fullWidth
            />
          </Box>
        </Box>
      </Paper>

      {/* Content */}
      <Paper sx={{ p: 3, mb: 3 }}>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={6}>
            <LinearProgress sx={{ width: '100%', maxWidth: 400, mb: 2, borderRadius: 1 }} />
            <Typography variant="body2" color="text.secondary">Loading experts...</Typography>
          </Box>
        ) : suggestedExperts.length === 0 ? (
          <Box textAlign="center" py={6}>
            <ScienceIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No matching experts found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There are currently no researchers with skills or interests matching this call.
            </Typography>
          </Box>
        ) : (
          <>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography variant="subtitle1" fontWeight={600}>
                {suggestedExperts.length} {suggestedExperts.length === 1 ? 'Expert' : 'Experts'} Found
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <ScienceIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  Matching by skills & interests
                </Typography>
              </Box>
            </Box>
            <Grid container spacing={2}>
              {suggestedExperts.map((expert, i) => (
                <Grid item xs={12} sm={4} key={expert.id || i}>
                  <ExpertCard onClick={() => handleExpertClick(expert.id)}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar
                          src={expert.avatar || expert.profile_image}
                          alt={`${expert.first_name} ${expert.last_name}`}
                          sx={{
                            width: 64,
                            height: 64,
                            fontSize: '1.5rem',
                            backgroundColor: theme.palette.primary.avatar,
                            color: 'white',
                            flexShrink: 0
                          }}
                        />
                        <Box flex={1} minWidth={0}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                            <Typography variant="subtitle1" fontWeight={600} noWrap>
                              {expert.first_name} {expert.last_name}
                            </Typography>
                            <StarIcon sx={{ fontSize: 18, color: 'warning.main', opacity: 0.8, ml: 1, flexShrink: 0 }} />
                          </Box>
                          {expert.organization_name && (
                            <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
                              {expert.organization_name}
                            </Typography>
                          )}
                          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" mt={1}>
                            {expert.skill_matches > 0 && (
                              <Chip
                                label={`${expert.skill_matches} skill${expert.skill_matches === 1 ? '' : 's'}`}
                                size="small"
                                color="primary"
                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 500 }}
                              />
                            )}
                            {expert.interest_matches > 0 && (
                              <Chip
                                label={`${expert.interest_matches} interest${expert.interest_matches === 1 ? '' : 's'}`}
                                size="small"
                                color="secondary"
                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 500, color: "#fff" }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </ExpertCard>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default AllSuggestedExpertsPage;
